/**
 * Banners Management Page - SuperAdmin
 * Gestión de banners publicitarios para el marketplace
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { 
  Plus, Trash2, Edit, Eye, EyeOff, Image as ImageIcon, 
  Link as LinkIcon, GripVertical, Upload, Loader2, X, Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const BannersManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    link_url: '',
    position: 'carousel_3col',
    order: 0,
    active: true,
    overlay_title: '',
    overlay_subtitle: '',
    text_color: '#ffffff',
    text_position: 'left',
    title_size: 'md',
    subtitle_size: 'sm',
    text_max_width: '100',
    text_align: 'left'
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const response = await api.get('/banners');
      setBanners(response.data);
    } catch (error) {
      console.error('Error loading banners:', error);
      toast.error('Error al cargar banners');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingBanner(null);
    setForm({
      title: '',
      link_url: '',
      position: 'carousel_3col',
      order: banners.length,
      active: true,
      overlay_title: '',
      overlay_subtitle: '',
      text_color: '#ffffff',
      text_position: 'left',
      title_size: 'md',
      subtitle_size: 'sm',
      text_max_width: '100',
      text_align: 'left'
    });
    setShowModal(true);
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setForm({
      title: banner.title || '',
      link_url: banner.link_url || '',
      position: banner.position || 'carousel_3col',
      order: banner.order || 0,
      active: banner.active ?? true,
      overlay_title: banner.overlay_title || '',
      overlay_subtitle: banner.overlay_subtitle || '',
      text_color: banner.text_color || '#ffffff',
      text_position: banner.text_position || 'left',
      title_size: banner.title_size || 'md',
      subtitle_size: banner.subtitle_size || 'sm',
      text_max_width: banner.text_max_width || '100',
      text_align: banner.text_align || 'left'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim()) {
      toast.error('El título es requerido');
      return;
    }
    
    try {
      if (editingBanner) {
        await api.put(`/banners/${editingBanner.id}`, form);
        toast.success('Banner actualizado');
      } else {
        await api.post('/banners', form);
        toast.success('Banner creado. Ahora sube una imagen.');
      }
      loadBanners();
      setShowModal(false);
    } catch (error) {
      toast.error('Error al guardar banner');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('¿Eliminar este banner?')) return;
    
    try {
      await api.delete(`/banners/${bannerId}`);
      toast.success('Banner eliminado');
      loadBanners();
    } catch (error) {
      toast.error('Error al eliminar banner');
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await api.put(`/banners/${banner.id}`, { active: !banner.active });
      toast.success(banner.active ? 'Banner desactivado' : 'Banner activado');
      loadBanners();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleImageUpload = async (bannerId, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(bannerId);
    
    try {
      await api.post(`/banners/${bannerId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Imagen subida y comprimida');
      loadBanners();
    } catch (error) {
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const heroBanners = banners.filter(b => b.position === 'hero_full');
  const carouselBanners = banners.filter(b => b.position === 'carousel_3col');
  const rowBanners = banners.filter(b => b.position === 'row_2col');

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Gestión de Banners</h1>
            <p className="text-slate-500">Administra los banners publicitarios del marketplace</p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            data-testid="create-banner-btn"
          >
            <Plus className="w-5 h-5" />
            Nuevo Banner
          </button>
        </div>

        {/* Hero Section (full-width) */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-800">Hero Banner (pantalla completa)</h2>
              <p className="text-sm text-slate-500">Banner principal con difuminado. Recomendado: 1400x500px</p>
            </div>
          </div>

          {heroBanners.length === 0 ? (
            <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No hay banners hero</p>
              <button
                onClick={() => { setForm(f => ({...f, position: 'hero_full'})); openCreateModal(); }}
                className="mt-2 text-blue-600 hover:underline text-sm"
              >
                Crear banner hero
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {heroBanners.map((banner) => (
                <BannerCard
                  key={banner.id}
                  banner={banner}
                  onEdit={() => openEditModal(banner)}
                  onDelete={() => handleDelete(banner.id)}
                  onToggle={() => handleToggleActive(banner)}
                  onImageUpload={(file) => handleImageUpload(banner.id, file)}
                  uploading={uploading === banner.id}
                  large
                />
              ))}
            </div>
          )}
        </div>

        {/* Carousel Section (3 columns) */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-800">Carrusel (3 columnas)</h2>
              <p className="text-sm text-slate-500">Tamaño recomendado: 400x200px</p>
            </div>
          </div>
          
          {carouselBanners.length === 0 ? (
            <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No hay banners en el carrusel</p>
              <button 
                onClick={() => { setForm(f => ({...f, position: 'carousel_3col'})); openCreateModal(); }}
                className="mt-2 text-blue-600 hover:underline text-sm"
              >
                Crear primer banner
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {carouselBanners.map((banner) => (
                <BannerCard
                  key={banner.id}
                  banner={banner}
                  onEdit={() => openEditModal(banner)}
                  onDelete={() => handleDelete(banner.id)}
                  onToggle={() => handleToggleActive(banner)}
                  onImageUpload={(file) => handleImageUpload(banner.id, file)}
                  uploading={uploading === banner.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Row Section (2 columns) */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-4 h-4 text-orange-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-800">Fila (2 columnas)</h2>
              <p className="text-sm text-slate-500">Tamaño recomendado: 600x250px</p>
            </div>
          </div>
          
          {rowBanners.length === 0 ? (
            <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No hay banners en la fila</p>
              <button 
                onClick={() => { setForm(f => ({...f, position: 'row_2col'})); openCreateModal(); }}
                className="mt-2 text-blue-600 hover:underline text-sm"
              >
                Crear primer banner
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rowBanners.map((banner) => (
                <BannerCard
                  key={banner.id}
                  banner={banner}
                  onEdit={() => openEditModal(banner)}
                  onDelete={() => handleDelete(banner.id)}
                  onToggle={() => handleToggleActive(banner)}
                  onImageUpload={(file) => handleImageUpload(banner.id, file)}
                  uploading={uploading === banner.id}
                  large
                />
              ))}
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-semibold text-lg">
                  {editingBanner ? 'Editar Banner' : 'Nuevo Banner'}
                </h3>
                <button 
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Título / Descripción
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm(f => ({...f, title: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Promoción de verano"
                    data-testid="banner-title-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Enlace (URL destino)
                  </label>
                  <input
                    type="url"
                    value={form.link_url}
                    onChange={(e) => setForm(f => ({...f, link_url: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://ejemplo.com/promocion"
                    data-testid="banner-link-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Opcional. Los usuarios serán redirigidos al hacer clic.</p>
                </div>
                
                {/* Texto sobre el banner */}
                <div className="border-t pt-4 mt-2">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Texto sobre el banner (opcional)</p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Título</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={form.overlay_title}
                          onChange={(e) => setForm(f => ({...f, overlay_title: e.target.value}))}
                          className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ej: Crea tu cuenta hoy"
                        />
                        <select
                          value={form.title_size}
                          onChange={(e) => setForm(f => ({...f, title_size: e.target.value}))}
                          className="w-24 px-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          title="Tamaño del título"
                        >
                          <option value="sm">Chico</option>
                          <option value="md">Mediano</option>
                          <option value="lg">Grande</option>
                          <option value="xl">XL</option>
                          <option value="2xl">2XL</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Subtítulo</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={form.overlay_subtitle}
                          onChange={(e) => setForm(f => ({...f, overlay_subtitle: e.target.value}))}
                          className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ej: Tu negocio en línea en minutos"
                        />
                        <select
                          value={form.subtitle_size}
                          onChange={(e) => setForm(f => ({...f, subtitle_size: e.target.value}))}
                          className="w-24 px-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          title="Tamaño del subtítulo"
                        >
                          <option value="xs">Chico</option>
                          <option value="sm">Mediano</option>
                          <option value="md">Grande</option>
                          <option value="lg">XL</option>
                          <option value="xl">2XL</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Color texto</label>
                        <div className="flex items-center gap-1">
                          <input
                            type="color"
                            value={form.text_color}
                            onChange={(e) => setForm(f => ({...f, text_color: e.target.value}))}
                            className="w-8 h-8 rounded border cursor-pointer"
                          />
                          <input
                            type="text"
                            value={form.text_color}
                            onChange={(e) => setForm(f => ({...f, text_color: e.target.value}))}
                            className="flex-1 px-2 py-1.5 border rounded-lg text-xs"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Ancho máx. recuadro</label>
                        <select
                          value={form.text_max_width}
                          onChange={(e) => setForm(f => ({...f, text_max_width: e.target.value}))}
                          className="w-full px-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="30">30%</option>
                          <option value="40">40%</option>
                          <option value="50">50%</option>
                          <option value="60">60%</option>
                          <option value="70">70%</option>
                          <option value="80">80%</option>
                          <option value="100">100%</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Posición recuadro</label>
                        <select
                          value={form.text_position}
                          onChange={(e) => setForm(f => ({...f, text_position: e.target.value}))}
                          className="w-full px-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="left">Izquierda</option>
                          <option value="center">Centro</option>
                          <option value="right">Derecha</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Alineación texto</label>
                        <select
                          value={form.text_align}
                          onChange={(e) => setForm(f => ({...f, text_align: e.target.value}))}
                          className="w-full px-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="left">Izquierda</option>
                          <option value="center">Centro</option>
                          <option value="right">Derecha</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Posición
                  </label>
                  <select
                    value={form.position}
                    onChange={(e) => setForm(f => ({...f, position: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="banner-position-select"
                  >
                    <option value="hero_full">Hero Banner (pantalla completa) - 1400x500px</option>
                    <option value="carousel_3col">Carrusel (3 columnas) - 400x200px</option>
                    <option value="row_2col">Fila (2 columnas) - 600x250px</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Orden
                  </label>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm(f => ({...f, order: parseInt(e.target.value) || 0}))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    data-testid="banner-order-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Los banners con orden menor aparecen primero.</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="banner-active"
                    checked={form.active}
                    onChange={(e) => setForm(f => ({...f, active: e.target.checked}))}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    data-testid="banner-active-checkbox"
                  />
                  <label htmlFor="banner-active" className="text-sm text-slate-700">
                    Banner activo (visible en el marketplace)
                  </label>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    data-testid="save-banner-btn"
                  >
                    {editingBanner ? 'Guardar cambios' : 'Crear banner'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Banner Card Component
const BannerCard = ({ banner, onEdit, onDelete, onToggle, onImageUpload, uploading, large = false }) => {
  const fileInputRef = React.useRef(null);
  
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageUpload(file);
    }
  };
  
  return (
    <div className={`bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow ${!banner.active ? 'opacity-60' : ''}`}>
      {/* Image Section */}
      <div 
        className={`relative bg-slate-100 ${large ? 'h-40' : 'h-28'} flex items-center justify-center`}
        style={{ aspectRatio: large ? '2.4/1' : '2/1' }}
      >
        {banner.image_url ? (
          <img 
            src={banner.image_url} 
            alt={banner.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center">
            <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-1" />
            <p className="text-xs text-slate-400">Sin imagen</p>
          </div>
        )}
        
        {/* Upload overlay */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="absolute inset-0 bg-black/0 hover:bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-all group"
        >
          {uploading ? (
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          ) : (
            <div className="text-white text-center">
              <Upload className="w-8 h-8 mx-auto mb-1" />
              <span className="text-sm">{banner.image_url ? 'Cambiar' : 'Subir'} imagen</span>
            </div>
          )}
        </button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        
        {/* Status badge */}
        <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${
          banner.active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
        }`}>
          {banner.active ? 'Activo' : 'Inactivo'}
        </div>
      </div>
      
      {/* Info Section */}
      <div className="p-3">
        <h3 className="font-medium text-slate-800 truncate">{banner.title || 'Sin título'}</h3>
        
        {banner.link_url && (
          <div className="flex items-center gap-1 text-xs text-slate-500 mt-1 truncate">
            <LinkIcon className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{banner.link_url}</span>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <span className="text-xs text-slate-400">Orden: {banner.order}</span>
          
          <div className="flex items-center gap-1">
            <button
              onClick={onToggle}
              className={`p-1.5 rounded-lg transition-colors ${
                banner.active 
                  ? 'hover:bg-amber-50 text-amber-600' 
                  : 'hover:bg-green-50 text-green-600'
              }`}
              title={banner.active ? 'Desactivar' : 'Activar'}
            >
              {banner.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button
              onClick={onEdit}
              className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
              title="Editar"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
              title="Eliminar"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannersManagement;
